import { useState, useEffect, useCallback } from "react";
import {
  Button, Space, Modal, Form, Select, DatePicker, Input, message,
} from "antd";
import { PlusOutlined, ReloadOutlined, FilePdfOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import ExportButton, { downloadBlob } from "../../components/common/ExportButton";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import { PartnerLabel } from "../../components/common/DeletedTag";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import useRiderOptions from "../../hooks/useRiderOptions";
import {
  listPledge, createPledge, updatePledge, deletePledge, downloadPledgePdf, exportPledge,
} from "../../services/payroll.service";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

export default function PledgeTab() {
  const { canEdit, canDelete } = usePermission("payrollExpenses");
  const { options: projectOptions } = useSettingsDropdown("projects");
  const { options: fleetOptions } = useSettingsDropdown("fleet-vehicle-types", {
    filter: (item) => item.category === "fleet",
  });
  const { options: riderOptions, loading: ridersLoading, onSearch: onRiderSearch } =
    useRiderOptions();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState(null);

  const [modal, setModal] = useState({ open: false, record: null });
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const params = useCallback(
    (extra = {}) => ({
      search: search || undefined,
      fromDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      toDate: dateRange?.[1]?.format("YYYY-MM-DD"),
      ...extra,
    }),
    [search, dateRange]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listPledge(params({ page, per_page: pageSize }));
      setRows(list?.data?.records || []);
      setTotalCount(list?.data?.pagination?.total_count || 0);
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not load pledge certificates");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [params, page, pageSize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    setPage(1);
  }, [search, dateRange]);

  const openModal = (record = null) => {
    setModal({ open: true, record });
    if (record) {
      form.setFieldsValue({
        rider: record.rider?._id,
        pledgeDate: record.pledgeDate ? dayjs(record.pledgeDate) : null,
        project: record.project?._id,
        fleetType: record.fleetType?._id,
        uuidNo: record.uuidNo,
        civilId: record.civilId,
        pledgeTo: record.pledgeTo,
        subject: record.subject,
        authorisedSignatory: record.authorisedSignatory,
        description: record.description,
        notes: record.notes,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ pledgeDate: dayjs() });
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = { ...values, pledgeDate: values.pledgeDate.format("YYYY-MM-DD") };
      if (modal.record) {
        await updatePledge(modal.record._id, payload);
        message.success("Pledge updated and PDF regenerated");
      } else {
        await createPledge(payload);
        message.success("Pledge issued");
      }
      setModal({ open: false, record: null });
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not save pledge");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (record) => {
    try {
      const blob = await downloadPledgePdf(record._id);
      downloadBlob(blob, `${record.pledgeTrackId}.pdf`);
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not download the certificate");
    }
  };

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: "Delete Pledge",
      content: `Delete ${record.pledgeTrackId}?`,
      onConfirm: async () => {
        try {
          await deletePledge(record._id);
          message.success("Pledge deleted");
          fetchAll();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Could not delete pledge");
        }
      },
    });
  };

  const columns = [
    { title: "Pledge Track ID", dataIndex: "pledgeTrackId" },
    { title: "Pledge Date", dataIndex: "pledgeDateLabel" },
    {
      title: "Partner",
      dataIndex: "partnerName",
      render: (_, r) => (
        <PartnerLabel name={`${r.partnerId} — ${r.partnerName}`} deleted={r.riderDeleted} />
      ),
    },
    { title: "Civil ID", dataIndex: "civilId" },
    { title: "UUID No", dataIndex: "uuidNo" },
    { title: "Pledge To", dataIndex: "pledgeTo", ellipsis: true },
    { title: "Subject", dataIndex: "subject", ellipsis: true },
    {
      title: "PDF",
      key: "pdf",
      width: 110,
      render: (_, record) => (
        <Button
          size="small"
          icon={<FilePdfOutlined />}
          onClick={() => handleDownload(record)}
        >
          Download
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search track ID, partner, subject"
          allowClear
          style={{ width: 280 }}
          onSearch={setSearch}
        />
        <RangePicker format="DD/MM/YYYY" onChange={setDateRange} />
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>
          Refresh
        </Button>
        <ExportButton
          moduleName="Pledge_Certificates"
          onExport={() => exportPledge(params())}
        />
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            Issue Pledge
          </Button>
        )}
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={setPage}
        actions={{
          onEdit: canEdit ? openModal : undefined,
          onDelete: canDelete ? handleDelete : undefined,
          canEdit,
          canDelete,
        }}
      />

      <Modal
        open={modal.open}
        title={modal.record ? "Edit Pledge" : "Issue Pledge"}
        onCancel={() => setModal({ open: false, record: null })}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        width={680}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="rider" label="Partner" rules={[{ required: true }]}>
            <Select
              showSearch
              filterOption={false}
              onSearch={onRiderSearch}
              loading={ridersLoading}
              options={riderOptions}
              placeholder="Search by partner ID or name"
            />
          </Form.Item>
          <Form.Item name="pledgeDate" label="Pledge Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="project" label="Project">
            <Select options={projectOptions} allowClear />
          </Form.Item>
          <Form.Item name="fleetType" label="Fleet Type">
            <Select options={fleetOptions} allowClear />
          </Form.Item>
          <Form.Item name="civilId" label="Civil ID">
            <Input placeholder="Defaults to the partner's stored civil ID" />
          </Form.Item>
          <Form.Item name="uuidNo" label="UUID No">
            <Input />
          </Form.Item>
          <Form.Item name="pledgeTo" label="Pledge To">
            <Input />
          </Form.Item>
          <Form.Item name="subject" label="Subject">
            <Input />
          </Form.Item>
          <Form.Item name="authorisedSignatory" label="Authorised Signatory">
            <Input placeholder="Name and title of the signing officer" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            extra="Supports {{rider}}, {{civilId}}, {{uuidNo}}, {{partnerId}}, {{date}} and <b>bold</b>."
          >
            <TextArea rows={5} maxLength={5000} showCount />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} maxLength={1000} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
